/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentProps, RefObject } from 'preact';
import type {
  CellContextMenuContext,
  SortCriterionDetail,
  TableContextMenuConfig
} from '#UNSAFE_TableView';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type { TableColumn } from '../Table';

import { useCallback, useRef, useState } from 'preact/hooks';
import { useContextMenuGesture } from '#hooks/UNSAFE_useContextMenuGesture';
import { Menu } from '#UNSAFE_Menu';
import { logicalCellExtractor, TABLE_FOCUSABLE_SELECTOR } from '../utils/TableDomUtils';
import { getCellContextMenuContextFromCell } from '../utils/TableFocusUtils';

type MenuProps = Pick<
  ComponentProps<typeof Menu>,
  'anchorRef' | 'initialFocus' | 'placement' | 'offsetValue' | 'isOpen'
>;

const TOUCH_OFFSET_VALUE = 40;

const menuPropGestureStates: Record<
  'mouse' | 'keyboard' | 'touch',
  Omit<Required<MenuProps>, 'anchorRef' | 'isOpen'>
> = {
  mouse: {
    initialFocus: 'menu',
    placement: 'bottom-start',
    offsetValue: 2
  },
  keyboard: {
    initialFocus: 'firstItem',
    placement: 'bottom-start',
    offsetValue: 0
  },
  touch: {
    initialFocus: 'menu',
    placement: 'end',
    offsetValue: TOUCH_OFFSET_VALUE
  }
};

const _initialMenuProps: MenuProps = {
  isOpen: false,
  initialFocus: 'menu',
  placement: 'bottom-start',
  offsetValue: 0,
  anchorRef: { current: null }
};

export const useContextMenu = <K extends string | number, D, C extends string>(
  rootRef: RefObject<HTMLDivElement>,
  tableId: string,
  tableData: CollectionData<K, D>[],
  columnsArray: TableColumn<K, D, C>[],
  contextMenuConfig?: TableContextMenuConfig<K, D, C>,
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[],
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void
) => {
  const [contextMenuContext, setContextMenuContext] = useState<CellContextMenuContext<K, D, C>>();
  const [menuProps, setMenuProps] = useState<MenuProps>(_initialMenuProps);
  const currentCellElementRef = useRef<HTMLElement>();

  const [isResizeDialogOpen, setIsResizeDialogOpen] = useState<boolean>(false);

  const { triggerProps } = useContextMenuGesture(
    ({ gesture, anchor, target }) => {
      if (rootRef.current) {
        // find the cell element the menu is being launched from
        const cellElement = (target as HTMLElement).closest(
          `[${TABLE_FOCUSABLE_SELECTOR}='${tableId}']`
        ) as HTMLElement;
        if (cellElement != null) {
          const targetCell = logicalCellExtractor<K, D, C>(cellElement, tableId, columnsArray);
          if (targetCell != null) {
            const cellContextMenuContext = getCellContextMenuContextFromCell<K, D, C>(
              tableData,
              columnsArray,
              setIsResizeDialogOpen,
              targetCell,
              sortCriterion,
              onSortCriterionChange
            );
            if (cellContextMenuContext != null) {
              currentCellElementRef.current = cellElement;
              const anchorBasedOnGesture = gesture === 'keyboard' ? cellElement : anchor;
              setContextMenuContext(cellContextMenuContext);
              setMenuProps({
                ...menuPropGestureStates[gesture],
                anchorRef: { current: anchorBasedOnGesture },
                isOpen: true
              });
            }
          }
        }
      }
    },
    { isDisabled: !contextMenuConfig }
  );

  const handleCloseContextMenu = useCallback<Required<ComponentProps<typeof Menu>>['onClose']>(
    (detail) => {
      // set focus back on the cell that was determined as the initial target
      if (detail.reason === 'dismissed' || detail.reason === 'itemAction') {
        currentCellElementRef.current?.focus({ preventScroll: true });
      }
      // close the menu
      setMenuProps({ ..._initialMenuProps });
    },
    []
  );

  return {
    menuProps: { ...menuProps, onClose: handleCloseContextMenu },
    contextMenuContext: contextMenuContext,
    contextMenuTriggerProps: triggerProps,
    isResizeDialogOpen: isResizeDialogOpen,
    setIsResizeDialogOpen: setIsResizeDialogOpen
  };
};
